import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import DocketFormDialog from "@/components/DocketFormDialog";
import DocketList from "@/components/DocketList";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { CalendarClock, Plus } from "lucide-react";
import { toast } from "sonner";

export function ClientDocket({ clientId, clientName }) {
  const [entries, setEntries] = useState([]);
  const [meta, setMeta] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/docket`).then((r) => setEntries(r.data)).catch(() => {});
  }, [clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/docket/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const onStatus = async (e, status) => {
    try { await api.patch(`/docket/${e.id}/status`, { status }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const confirmDelete = async () => {
    try { await api.delete(`/docket/${deleteTarget.id}`); toast.success("Entry deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="client-docket-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-gray-400" /> Docket
        </h3>
        <button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="client-add-docket-button"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#002FA7] hover:text-[#002480] border border-gray-200 hover:border-[#002FA7] px-2 py-1 rounded-sm transition-colors">
          <Plus className="w-3.5 h-3.5" /> New entry
        </button>
      </div>

      <DocketList entries={entries} onEdit={(e) => { setEditing(e); setDialogOpen(true); }}
        onDelete={setDeleteTarget} onStatus={onStatus} statuses={meta.statuses} />

      <DocketFormDialog open={dialogOpen} onOpenChange={setDialogOpen} entry={editing}
        fixedClientId={clientId} fixedClientName={clientName} meta={meta} onSaved={load} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-client-docket-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete docket entry?</AlertDialogTitle>
            <AlertDialogDescription>This entry will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-client-docket-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
